import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Landmark, Copy, Check, ArrowRight } from "lucide-react";
import { useState } from "react";
import halykLogo from "../assets/halyk_bank_logo.png";
import kaspiLogo from "../assets/kaspi_logo.png";
import GlassCard from "./ui/GlassCard";
import Button from "./ui/Button";

const KASPI_PAYMENT_LINK =
  "https://kaspi.kz/pay/PalatyUniversal?6118=6&6119=23810&started_from=share";

const BANKS = [
  {
    key: "halyk",
    logo: halykLogo,
    title: "Через Halyk Bank",
    fields: [
      ["Компания", 'ОО "Жасыл желкен"'],
      ["ИИН/БИН", "230540025862"],
      ["ИИК", "KZ56601A871013227121"],
      ["Банк", "АО «Народный Банк Казахстана»"],
      ["БИК", "HSBKKZKX"],
      ["Валюта", "KZT"],
      ["Назначение платежа", "Добровольное пожертвование"],
    ],
  },
  {
    key: "kaspi",
    logo: kaspiLogo,
    title: "Через Kaspi Bank",
    fields: [
      ["Компания", 'ОО "Жасыл желкен"'],
      ["БИН (ИИН)", "230540025862"],
      ["Банк", 'АО "Kaspi Bank"'],
      ["БИК", "CASPKZKA"],
      ["Номер счёта", "KZ26722S000045772704"],
      ["Назначение платежа", "Добровольное пожертвование"],
    ],
  },
];

export default function DonationDetails() {
  const navigate = useNavigate();
  const isAuthenticated = !!localStorage.getItem("token");

  const handlePayClick = () => {
    if (isAuthenticated) {
      window.open(KASPI_PAYMENT_LINK, "_blank", "noopener,noreferrer");
    } else {
      navigate("/login");
    }
  };

  return (
    <div className="min-h-screen bg-dark-50">
      {/* Header band */}
      <div className="relative overflow-hidden bg-gradient-hero pt-32 pb-20 px-6">
        <div className="pointer-events-none absolute -top-24 -left-24 w-72 h-72 bg-primary-400/30 rounded-full blur-3xl animate-float-slow" />
        <div className="pointer-events-none absolute -bottom-24 -right-16 w-80 h-80 bg-teal-400/20 rounded-full blur-3xl animate-float" />

        <div className="relative max-w-4xl mx-auto text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 mb-4 text-sm font-semibold tracking-wide uppercase rounded-full bg-white/15 text-white backdrop-blur-sm"
          >
            <Landmark className="w-4 h-4" />
            Реквизиты
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-3xl md:text-4xl font-bold text-white"
          >
            Реквизиты для пожертвований
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-3 text-white/80 max-w-xl mx-auto"
          >
            Переведите пожертвование напрямую по банковским реквизитам или через
            ссылку для взноса.
          </motion.p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 -mt-10 pb-20 relative z-10">
        <div className="grid md:grid-cols-2 gap-6">
          {BANKS.map((bank, i) => (
            <motion.div
              key={bank.key}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
            >
              <GlassCard hover={false} className="p-6 md:p-8 bg-white h-full">
                <img src={bank.logo} alt={bank.title} className="h-12 mb-4 mx-auto" />
                <h2 className="text-lg font-bold text-dark-900 mb-4 text-center">
                  {bank.title}
                </h2>
                <dl className="space-y-2.5">
                  {bank.fields.map(([label, value]) => (
                    <RequisiteRow key={label} label={label} value={value} />
                  ))}
                </dl>
              </GlassCard>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-8"
        >
          <GlassCard hover={false} className="p-6 md:p-10 bg-white text-center">
            <h3 className="text-xl font-bold text-dark-900 mb-2">
              Хотите стать частью движения ОО «Жасыл Желкен»?
            </h3>
            <p className="text-dark-500 max-w-md mx-auto mb-6">
              {isAuthenticated
                ? "Вы вошли в аккаунт — можно перейти к оплате взноса."
                : "Чтобы оплатить взнос, сначала нужно войти в аккаунт."}
            </p>
            <Button
              variant="primary"
              size="lg"
              icon={<img src={kaspiLogo} alt="Kaspi" className="w-5 h-5 object-contain" />}
              iconRight={<ArrowRight className="w-5 h-5" />}
              onClick={handlePayClick}
            >
              {isAuthenticated ? "Ссылка для взноса" : "Войти и оплатить"}
            </Button>
          </GlassCard>
        </motion.div>
      </div>
    </div>
  );
}

function RequisiteRow({ label, value }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — silently ignore
    }
  };

  return (
    <div className="flex items-start justify-between gap-3 rounded-xl bg-dark-50 px-4 py-2.5">
      <div className="min-w-0">
        <dt className="text-xs font-medium text-dark-400 uppercase tracking-wide">{label}</dt>
        <dd className="text-sm font-semibold text-dark-900 break-words">{value}</dd>
      </div>
      <button
        type="button"
        onClick={handleCopy}
        className={`shrink-0 mt-4 w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
          copied
            ? "text-primary-600 bg-primary-50"
            : "text-dark-400 hover:text-primary-600 hover:bg-white"
        }`}
        aria-label={`Скопировать: ${label}`}
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}
